import { useEffect } from 'react';
import { useCellValues, usePublisher, imageDialogState$, saveImage$, closeImageDialog$ } from '@mdxeditor/editor';
import { useForm, FormProvider } from 'react-hook-form';
import ImageSelector from '../Homepage/HomeForm/ImageSelector';
import { FormField } from '../../../components/FormField';

interface ImageFormData {
  image?: { public_url: string; label?: string; description?: string } | null;
  altText?: string;
  title?: string;
}

export default function CustomImageDialog() {
  const [state] = useCellValues(imageDialogState$);
  const saveImage = usePublisher(saveImage$);
  const closeImageDialog = usePublisher(closeImageDialog$);

  const methods = useForm<ImageFormData>({
    values: state.type === 'editing'
      ? {
        image: state.initialValues.src ? { public_url: state.initialValues.src } : null,
        altText: state.initialValues.altText,
        title: state.initialValues.title
      }
      : {}
  });

  const { handleSubmit, watch, setValue } = methods;
  const selectedImage = watch('image');

  useEffect(() => {
    console.log('selectedImage', selectedImage)
    if (selectedImage && state.type === 'new') {
      if (selectedImage.label) {
        setValue('title', selectedImage.label);
      }
      if (selectedImage.description) {
        setValue('altText', selectedImage.description);
      }
    }
  }, [selectedImage, setValue, state.type]);

  if (state.type === 'inactive') {
    return null;
  }

  const onSubmit = (data: ImageFormData) => {
    saveImage({
      src: data.image?.public_url,
      altText: data.altText,
      title: data.title
    });
  };

  return (
    <FormProvider {...methods}>
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}>
        <div
          className="shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          style={{
            backgroundColor: '#f5f5f5',
          }}
        >
          <div>
            <div style={{
              padding: '4px',
              textAlign: 'center',
            }}>
              <div className="text-md font-bold" style={{
                color: 'var(--color-neutral)'
              }}>
                {state.type === 'editing' ? 'Edit Image' : 'Insert Image'}
              </div>
            </div>

            <hr />

            <form
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.shiftKey && e.key === 'Enter') {
                  e.preventDefault()
                  handleSubmit(onSubmit)
                }
              }}
            >
              <div style={{
                padding: '4px',
                borderBottom: '1px solid #ddd',
                backgroundColor: 'white',
              }}>
                <FormField.Container style={{
                  backgroundColor: 'var(--color-base-300)',
                  padding: '4px',
                }}>
                  {/* Image Selector */}
                  <ImageSelector name="image" />

                  {/* Alt Text */}
                  <FormField.Field
                    name="altText"
                    label="Alt Text (required for accessibility)"
                    placeholder="Describe the image"
                    registerProps={{ required: 'Alt text is required for accessibility' }}
                  />

                  {/* Title */}
                  <FormField.Field
                    name="title"
                    label="Title (optional)"
                    placeholder="Image title"
                  />
                </FormField.Container>

              </div>
              <hr />

              {/* Action Buttons */}
              <div
                className="flex justify-end gap-1"
                style={{
                  padding: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <button
                  type="button"
                  onClick={() => closeImageDialog()}
                  className="btn btn-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  className="btn btn-sm btn-primary"
                  disabled={!selectedImage?.public_url}
                >
                  {state.type === 'editing' ? 'Update' : 'Insert'}
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    </FormProvider>
  );
}